import { useState, type ReactElement } from 'react';

import { useMasterList } from '@/features/master-list/use-master-list';

import { EVENT_TYPES, PURPOSES, type PrintLogFilters as Filters } from '../types';
import { eventBadge, purposeLabel } from '../presentation';
import { Select } from '@/components/ui/Select';

import { PersonFilterField } from './PersonFilterField';
import { errorAdvice } from '@/lib/error-advice';

interface Props {
  companyId: string;
  filters: Filters;
  active: boolean;
  /** Código del documento filtrado, si se conoce (para mostrarlo en vez del id). */
  docCode: string | null;
  /** Nombre de la persona filtrada, si se conoce (G1-3). */
  personName: string | null;
  onChange: (patch: Partial<Filters>) => void;
  onClear: () => void;
}

const FOLIO = /^[0-9A-HJKMNP-TV-Z]{10}$/i;

/** Filtros de la bitácora: documento (búsqueda en la Lista Maestra), persona, evento,
 *  tipo de copia, rango de fechas y folio. Todo va a la URL. */
export function PrintLogFilters({
  companyId,
  filters,
  active,
  docCode,
  personName,
  onChange,
  onClear,
}: Props): ReactElement {
  const [q, setQ] = useState('');
  const [folio, setFolio] = useState(filters.folio ?? '');
  const search = q.trim();
  const folioHint = folio !== '' && !FOLIO.test(folio);
  const docs = useMasterList(
    { limit: 10, page: 1, q: search },
    { companyId, enabled: search.length >= 2 && filters.doc === null },
  );

  return (
    <div className="cb logfilters">
      <div className="fld">
        <span className="fld-lab">Documento</span>
        {filters.doc !== null ? (
          <span className="chipsel">
            <span className="code">{docCode ?? 'Documento seleccionado'}</span>
            <button type="button" className="btn mut sm" onClick={() => onChange({ doc: null })}>
              Quitar
            </button>
          </span>
        ) : (
          <>
            <input
              type="search"
              className="selector fw"
              aria-label="Buscar documento por código o título"
              placeholder="Código o título"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {/* Mismos estados que el campo de persona: un fallo no se ve como "sin resultados". */}
            {search.length >= 2 && docs.isFetching && docs.data === undefined && !docs.isError && (
              <span className="soft" role="status">
                Buscando documentos…
              </span>
            )}
            {search.length >= 2 && docs.isError && (
              <span className="fld-err" role="alert">
                No se pudo buscar. {errorAdvice(docs.error)}
              </span>
            )}
            {search.length >= 2 && !docs.isError && docs.data?.data.length === 0 && (
              <span className="soft">Ningún documento coincide con la búsqueda.</span>
            )}
            {search.length >= 2 && (docs.data?.data.length ?? 0) > 0 && (
              <ul className="doc-picker" aria-label="Documentos encontrados">
                {docs.data?.data.map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      className="doc-opt"
                      onClick={() => {
                        onChange({ doc: d.id });
                        setQ('');
                      }}
                    >
                      <span className="code">{d.code}</span> {d.title ?? ''}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
      <PersonFilterField
        companyId={companyId}
        value={filters.persona}
        name={personName}
        onChange={(persona) => onChange({ persona })}
      />
      <label className="fld">
        <span className="fld-lab">Evento</span>
        <Select
          value={filters.tipo ?? ''}
          options={[
            { value: '', label: 'Todos' },
            ...EVENT_TYPES.map((t) => ({ value: t, label: eventBadge(t).label })),
          ]}
          onChange={(v) => onChange({ tipo: (v || null) as Filters['tipo'] })}
        />
      </label>
      <label className="fld">
        <span className="fld-lab">Tipo de copia</span>
        <Select
          value={filters.proposito ?? ''}
          options={[
            { value: '', label: 'Todas' },
            ...PURPOSES.map((p) => ({ value: p, label: purposeLabel(p) })),
          ]}
          onChange={(v) => onChange({ proposito: (v || null) as Filters['proposito'] })}
        />
      </label>
      <label className="fld">
        <span className="fld-lab">Desde</span>
        <input
          type="date"
          className="selector fw"
          value={filters.desde ?? ''}
          max={filters.hasta ?? undefined}
          onChange={(e) => onChange({ desde: e.target.value || null })}
        />
      </label>
      <label className="fld">
        <span className="fld-lab">Hasta</span>
        <input
          type="date"
          className="selector fw"
          value={filters.hasta ?? ''}
          min={filters.desde ?? undefined}
          onChange={(e) => onChange({ hasta: e.target.value || null })}
        />
      </label>
      <label className="fld">
        <span className="fld-lab">Folio</span>
        <input
          type="search"
          className="selector fw"
          placeholder="10 caracteres"
          maxLength={10}
          value={folio}
          aria-invalid={folioHint}
          aria-describedby={folioHint ? 'print-log-folio-hint' : undefined}
          onChange={(e) => {
            const v = e.target.value.trim();
            setFolio(v);
            // Lo que se ve es lo que filtra: un folio incompleto quita el filtro aplicado.
            const next = FOLIO.test(v) ? v.toUpperCase() : null;
            if (next !== filters.folio) {
              onChange({ folio: next });
            }
          }}
        />
        {folioHint && (
          <span id="print-log-folio-hint" className="fld-err">
            10 caracteres: números y letras, sin I, L, O ni U.
          </span>
        )}
      </label>
      {active && (
        <button
          type="button"
          className="btn mut sm logclear"
          onClick={() => {
            setQ('');
            setFolio('');
            onClear();
          }}
        >
          Limpiar filtros
        </button>
      )}
    </div>
  );
}
